import { defineStore } from 'pinia';
import { ref } from 'vue';

import DashboardAPI from '@/apis/dashboards';
import { MULTI_CHANNEL_CONTENT_KEY, MULTI_CHANNEL_KEY } from '@/models/dashboards/channels.enum';
import { useMetricDetailsQuery } from '@/shared/queries/metric-details';
import { logger } from '@/utils/logger';

import { useFlagStore } from './flag';

// TODO: Move metric types and comparable metrics into this store
export const useMetricsStore = defineStore('metrics', () => {
  // Writable refs mirrored from the query so consumers (and tests) stay unchanged.
  const metricDetails = ref({});
  const metricDetailsReady = ref(false);
  const metricDetailsError = ref(false);

  const legacyMetricDetails = ref({});
  const legacyMetricDetailsReady = ref(false);
  const legacyMetricDetailsError = ref(false);

  async function fetchMetricDetails() {
    const query = useMetricDetailsQuery();
    // refresh() dedupes and only refetches when stale.
    await query.refresh();
    if (query.data.value) {
      metricDetails.value = query.data.value;
      metricDetailsReady.value = true;
      metricDetailsError.value = false;
    } else if (query.error.value) {
      metricDetailsError.value = true;
      logger.error('Failed to fetch metric details', {}, query.error.value);
    }
  }

  function getMetricKeysByParent(
    channel,
    metricReportTypeKey,
    parentMetric,
    includeHidden = false,
  ) {
    const flagStore = useFlagStore();
    const allChannelTypeMetrics = metricDetails.value?.[channel]?.[metricReportTypeKey];
    return Object.keys(allChannelTypeMetrics).filter((metricKey) => {
      const {
        parent_metric: currentParentMetric,
        hide_on_dashboards: hideOnDashboards,
        required_feature_flag: requiredFeatureFlag,
      } = allChannelTypeMetrics[metricKey];
      const parentMetricMatch = metricKey === parentMetric || currentParentMetric === parentMetric;
      const hasRequiredFlag = Boolean(
        !requiredFeatureFlag || (flagStore.ready && flagStore.flags?.[requiredFeatureFlag]),
      );
      return (includeHidden || !hideOnDashboards) && parentMetricMatch && hasRequiredFlag;
    }, {});
  }

  function getTargetMetricDetails(channel, metricReportTypeKey, metric) {
    return metricDetails.value?.[channel]?.[metricReportTypeKey]?.[metric];
  }

  function getTargetMetricDetailsMultiChannel(metric, isContentReport = false) {
    if (isContentReport) return metricDetails.value?.[MULTI_CHANNEL_CONTENT_KEY]?.[metric];
    return metricDetails.value?.[MULTI_CHANNEL_KEY]?.[metric];
  }

  /**
   * NOTE: This hopefully will be a temporary solution until we can move custom metrics to the new
   * metric details format. Do not propagate this pattern if possible.
   */
  async function initializeLegacyMetricDetails() {
    if (!legacyMetricDetails.value) return;
    legacyMetricDetailsError.value = false;
    try {
      const response = await DashboardAPI.getMetricDetails({ legacyCopy: true });
      legacyMetricDetails.value = response?.data;
      legacyMetricDetailsReady.value = true;
    } catch (err) {
      legacyMetricDetailsError.value = true;
      logger.error('Failed to fetch legacy metric details', {}, err);
    }
  }

  return {
    metricDetailsReady,
    metricDetailsError,
    metricDetails,
    fetchMetricDetails,
    getMetricKeysByParent,
    getTargetMetricDetails,
    getTargetMetricDetailsMultiChannel,
    initializeLegacyMetricDetails,
    legacyMetricDetails,
    legacyMetricDetailsReady,
    legacyMetricDetailsError,
  };
});
